import React, { useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Switch, FormControlLabel,
  RadioGroup, Radio, Alert, Divider,
} from '@mui/material';
import Navbar from '../components/Navbar';
import RequireAuth from '../components/athletes/RequireAuth';
import { useAuth } from '../context/AuthContext';
import { CAREER_MODES, CAREER_MODE_META } from '../utils/careerModes';

// Phase C.1b — the smallest sensible home for the Career Mode selector.
// No account/profile settings page existed anywhere in this app before
// this phase (confirmed via search) — this is deliberately minimal (no
// name/email/password editing, no onboarding wizard) and exists only to
// host: (1) which of the four Career Modes this account has enabled,
// (2) which enabled one is active/preferred. Both are pure experience
// preferences — see utils/careerModes.js / migration 037's own header
// for why enabling a mode here grants ZERO Team/Program/guardian/athlete
// authority.
function CareerModeSettings() {
  const { careerModes, activeCareerMode, enableCareerMode, disableCareerMode, setActiveMode } = useAuth();
  const [pending, setPending] = useState(null); // mode currently being toggled/activated
  const [error, setError] = useState(null);

  const handleToggle = async (mode, enabled) => {
    setError(null);
    setPending(mode);
    try {
      if (enabled) await enableCareerMode(mode);
      else await disableCareerMode(mode);
    } catch (e) {
      setError(e.message || 'Could not update Career Mode.');
    } finally {
      setPending(null);
    }
  };

  const handleActivate = async (mode) => {
    setError(null);
    setPending(`active:${mode}`);
    try {
      await setActiveMode(mode);
    } catch (e) {
      setError(e.message || 'Could not set active Career Mode.');
    } finally {
      setPending(null);
    }
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Career Modes</Typography>
        <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', mb: 2 }}>
          Choose how you participate in football on Football for Nerds. You can enable more than
          one — this only changes how FFN presents itself to you, never what you're authorized to
          access or manage. Team, Program, and guardian access always come from your actual
          relationships, not from the modes below.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {CAREER_MODES.map(mode => {
            const meta = CAREER_MODE_META[mode];
            const enabled = careerModes.includes(mode);
            return (
              <Box key={mode} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      disabled={pending === mode}
                      onChange={e => handleToggle(mode, e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{meta.emoji} {meta.label}</Typography>
                      <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>{meta.description}</Typography>
                    </Box>
                  }
                />
              </Box>
            );
          })}
        </Box>

        {careerModes.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Active Mode</Typography>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem', mb: 1.5 }}>
              Which enabled mode FFN should emphasize right now.
            </Typography>
            <RadioGroup
              value={activeCareerMode || ''}
              onChange={e => handleActivate(e.target.value)}
            >
              {CAREER_MODES.filter(m => careerModes.includes(m)).map(mode => (
                <FormControlLabel
                  key={mode}
                  value={mode}
                  disabled={pending === `active:${mode}`}
                  control={<Radio />}
                  label={`${CAREER_MODE_META[mode].emoji} ${CAREER_MODE_META[mode].label}`}
                />
              ))}
            </RadioGroup>
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </CardContent>
    </Card>
  );
}

function AccountSettingsInner() {
  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 3 }}>Account Settings</Typography>
      <CareerModeSettings />
    </Container>
  );
}

export default function AccountSettingsPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth message="Sign in to manage your Football for Nerds account.">
        <AccountSettingsInner />
      </RequireAuth>
    </Box>
  );
}
